export interface PasswordStrength {
  strength: number;
  label: string;
  color: string;
  percentage: number;
}

export interface PasswordRequirement {
  met: boolean;
  label: string;
}

export interface PasswordValidation {
  isValid: boolean;
  errors: string[];
  strength: PasswordStrength;
  requirements: PasswordRequirement[];
}

/**
 * Calculate password strength based on multiple criteria
 */
export const calculatePasswordStrength = (password: string, t: (key: string) => string): PasswordStrength => {
  if (!password) {
    return { 
      strength: 0, 
      label: "", 
      color: "", 
      percentage: 0 
    };
  }
  
  let strength = 0;
  
  // Length criteria
  if (password.length >= 6) strength++;
  if (password.length >= 8) strength++;
  
  // Character variety criteria
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  if (/[^A-Za-z0-9]/.test(password)) strength++;

  const percentage = (strength / 5) * 100;

  if (strength <= 2) {
    return { 
      strength, 
      label: t("changePassword.weak"), 
      color: "text-destructive",
      percentage 
    };
  }
  
  if (strength <= 3) {
    return { 
      strength, 
      label: t("changePassword.medium"), 
      color: "text-yellow-500",
      percentage 
    };
  }
  
  return { 
    strength, 
    label: t("changePassword.strong"), 
    color: "text-green-500",
    percentage 
  };
};

/**
 * Get password requirements with their current status
 */
export const getPasswordRequirements = (password: string, t: (key: string) => string): PasswordRequirement[] => {
  return [
    {
      met: password.length >= 6,
      label: t("changePassword.requirement1")
    },
    {
      met: /[A-Z]/.test(password),
      label: t("changePassword.requirement2")
    },
    {
      met: /\d/.test(password),
      label: t("changePassword.requirement3")
    }
  ];
};

/**
 * Validate password change form data
 */
export const validatePasswordChange = (
  currentPassword: string,
  newPassword: string,
  confirmPassword: string,
  t: (key: string) => string
): PasswordValidation => {
  const errors: string[] = [];
  
  // Check if all fields are filled
  if (!currentPassword || !newPassword || !confirmPassword) {
    errors.push(t("changePassword.fillAllFields"));
  }
  
  // Check minimum length
  if (newPassword && newPassword.length < 6) {
    errors.push(t("changePassword.passwordTooShort"));
  }
  
  // Check if passwords match
  if (newPassword && confirmPassword && newPassword !== confirmPassword) {
    errors.push(t("changePassword.passwordsMismatch"));
  }
  
  const strength = calculatePasswordStrength(newPassword, t);
  const requirements = getPasswordRequirements(newPassword, t);
  
  return {
    isValid: errors.length === 0,
    errors,
    strength,
    requirements
  };
};

/**
 * Get strength bar color class based on strength level
 */
export const getStrengthBarColor = (strength: number): string => {
  if (strength <= 2) return "bg-destructive";
  if (strength <= 3) return "bg-yellow-500";
  return "bg-green-500";
};
